import controlEn from './legacy-locales/en.json';
import experimentEn from './locales/en.json';

export const LOCALIZATION_GATE_ID = 'i18n-admin';

export type LocalizationMode = 'control' | 'experiment';

const CONTROL_ADDITIONAL_LOCALES = ['fr', 'id', 'pt-BR'] as const;
const CONTROL_SUPPORTED_LOCALES = [
  'en',
  ...CONTROL_ADDITIONAL_LOCALES,
] as const;

const EXPERIMENT_ADDITIONAL_LOCALES = [
  'pt-BR',
  'es',
  'id-ID',
  'fr-FR',
  'de-DE',
  'it-IT',
  'tr-TR',
  'hi-IN',
  'ms-MY',
] as const;
const EXPERIMENT_SUPPORTED_LOCALES = [
  'en',
  ...EXPERIMENT_ADDITIONAL_LOCALES,
] as const;

export type ControlLocale = (typeof CONTROL_SUPPORTED_LOCALES)[number];
export type ExperimentLocale = (typeof EXPERIMENT_SUPPORTED_LOCALES)[number];
export type LocaleForMode<M extends LocalizationMode> = M extends 'control'
  ? ControlLocale
  : ExperimentLocale;

type LocaleLoader = () => Promise<{ default: Record<string, unknown> }>;

interface ModeConfig<Locale extends string> {
  supportedLocales: readonly Locale[];
  aliasMap: Record<string, Locale>;
  baseResources: {
    en: {
      translation: Record<string, unknown>;
    };
  };
  loaders: Partial<Record<Locale, LocaleLoader>>;
}

export const MODE_CONFIG = {
  control: {
    supportedLocales: CONTROL_SUPPORTED_LOCALES,
    aliasMap: {
      en: 'en',
      'en-us': 'en',
      fr: 'fr',
      'fr-fr': 'fr',
      id: 'id',
      'id-id': 'id',
      pt: 'pt-BR',
      'pt-br': 'pt-BR',
    },
    baseResources: {
      en: { translation: controlEn },
    },
    loaders: {
      fr: () => import('./legacy-locales/fr.json'),
      id: () => import('./legacy-locales/id.json'),
      'pt-BR': () => import('./legacy-locales/pt-BR.json'),
    },
  } satisfies ModeConfig<ControlLocale>,
  experiment: {
    supportedLocales: EXPERIMENT_SUPPORTED_LOCALES,
    aliasMap: {
      en: 'en',
      'en-us': 'en',
      fr: 'fr-FR',
      'fr-fr': 'fr-FR',
      id: 'id-ID',
      'id-id': 'id-ID',
      pt: 'pt-BR',
      'pt-br': 'pt-BR',
      es: 'es',
      de: 'de-DE',
      'de-de': 'de-DE',
      it: 'it-IT',
      'it-it': 'it-IT',
      tr: 'tr-TR',
      'tr-tr': 'tr-TR',
      hi: 'hi-IN',
      'hi-in': 'hi-IN',
      ms: 'ms-MY',
      'ms-my': 'ms-MY',
    },
    baseResources: {
      en: { translation: experimentEn },
    },
    loaders: {
      'pt-BR': () => import('./locales/pt-BR.json'),
      es: () => import('./locales/es.json'),
      'id-ID': () => import('./locales/id-ID.json'),
      'fr-FR': () => import('./locales/fr-FR.json'),
      'de-DE': () => import('./locales/de-DE.json'),
      'it-IT': () => import('./locales/it-IT.json'),
      'tr-TR': () => import('./locales/tr-TR.json'),
      'hi-IN': () => import('./locales/hi-IN.json'),
      'ms-MY': () => import('./locales/ms-MY.json'),
    },
  } satisfies ModeConfig<ExperimentLocale>,
} as const;
